import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { sessionByStaffPin } from "../../api/user";
import { getFirebaseBackend } from "../../helpers/firebase_helper";
import {
  HANDHELD_SWITCH_EVENT,
  clearHandheldPinLock,
  isHandheldPinSession,
  readHandheldEnterprise,
  readHandheldPin,
  rememberHandheldEnterprise,
  shouldLockOnResume,
  writeHandheldPin,
} from "../../helpers/handheldPin";
import { normalizeStaffPin, staffPinError } from "../../helpers/staffPin";
import { staffIdleLockDisplayName } from "../../helpers/staffIdleLock";
import { isHandheldStaffUser } from "../../helpers/handheldMode";
import "./HandheldShell.scss";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "←", "0", "OK"];

const sessionBody = (res) => {
  if (res?.data?.customToken) return res.data;
  if (res?.customToken) return res;
  return res?.data || res || {};
};

/**
 * Wake from sleep, Switch user, and signed-out gun login all land here.
 * A different employee's PIN signs the gun in as them. After logout the
 * pad is the only login — never the email screen.
 */
const HandheldPinGate = ({ asLogin = false }) => {
  const location = useLocation();
  const user = useSelector((s) => s?.currentUser?.user || {});
  const enterprise = useSelector((s) => s?.currentUser?.enterprise || {});
  const signedIn = Boolean(user?.uid);
  const sellerAccount = signedIn && !isHandheldStaffUser(user);
  const onScanner =
    !sellerAccount &&
    (asLogin ||
      isHandheldPinSession({
        pathname: location.pathname,
        search: location.search,
      }));
  const [mode, setMode] = useState(asLogin ? "login" : null);
  const [pin, setPin] = useState("");
  const pinRef = useRef("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  pinRef.current = pin;

  const lock = useCallback((next) => {
    setMode(next);
    setPin("");
    setError("");
    writeHandheldPin({ locked: true, mode: next, hiddenAt: Date.now() });
  }, []);

  const unlock = useCallback(() => {
    setMode(null);
    setPin("");
    setError("");
    clearHandheldPinLock();
  }, []);

  useEffect(() => {
    const id = user?.enterpriseId || enterprise?.uid || enterprise?.id;
    if (id) rememberHandheldEnterprise(id);
  }, [user?.enterpriseId, enterprise?.uid, enterprise?.id]);

  useEffect(() => {
    if (!onScanner) return undefined;
    if (asLogin) {
      lock("login");
      return undefined;
    }
    if (!signedIn) return undefined;
    const stored = readHandheldPin();
    if (stored?.locked || shouldLockOnResume(stored?.hiddenAt, Date.now())) {
      lock(stored?.mode === "switch" ? "switch" : "wake");
    }
    return undefined;
  }, [onScanner, asLogin, signedIn, lock]);

  useEffect(() => {
    if (!onScanner || mode || !signedIn) return undefined;

    const markHidden = () => {
      const prev = readHandheldPin();
      if (prev?.locked) return;
      writeHandheldPin({ locked: false, hiddenAt: Date.now() });
    };
    const markVisible = () => {
      const stored = readHandheldPin();
      if (stored?.locked || shouldLockOnResume(stored?.hiddenAt, Date.now())) {
        lock(stored?.mode === "switch" ? "switch" : "wake");
        return;
      }
      if (stored?.hiddenAt) clearHandheldPinLock();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") markHidden();
      else markVisible();
    };
    const onPageShow = () => markVisible();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", markHidden);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", markHidden);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [onScanner, mode, lock]);

  useEffect(() => {
    if (!onScanner) return undefined;
    const onSwitch = () => lock("switch");
    window.addEventListener(HANDHELD_SWITCH_EVENT, onSwitch);
    return () => window.removeEventListener(HANDHELD_SWITCH_EVENT, onSwitch);
  }, [onScanner, lock]);

  useEffect(() => {
    if (!mode) {
      delete document.documentElement.dataset.hhPin;
      return undefined;
    }
    document.documentElement.dataset.hhPin = "1";
    return () => {
      delete document.documentElement.dataset.hhPin;
    };
  }, [mode]);

  const submit = useCallback(
    async (value) => {
      const formatErr = staffPinError(value);
      if (formatErr) {
        setError(formatErr);
        return;
      }
      setBusy(true);
      setError("");
      try {
        const body = sessionBody(
          await sessionByStaffPin(normalizeStaffPin(value), {
            skipAuth: !signedIn,
            enterpriseId: readHandheldEnterprise() || undefined,
          })
        );
        if (!body?.uid || !body?.customToken) {
          throw new Error("No team member found for that PIN.");
        }
        if (body.enterpriseId) rememberHandheldEnterprise(body.enterpriseId);
        if (signedIn && body.uid === user?.uid) {
          unlock();
          return;
        }
        clearHandheldPinLock();
        await getFirebaseBackend().loginUser(null, null, body.customToken);
        window.location.replace("/handheld");
      } catch (err) {
        setError(
          err?.response?.data?.message || err?.message || "Could not verify PIN."
        );
        setPin("");
      } finally {
        setBusy(false);
      }
    },
    [unlock, user?.uid, signedIn]
  );

  useEffect(() => {
    if (!mode) return undefined;
    const onKey = (e) => {
      if (busy) return;
      if (e.key === "Backspace") {
        e.preventDefault();
        e.stopPropagation();
        setPin((p) => p.slice(0, -1));
        setError("");
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        submit(pinRef.current);
        return;
      }
      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        setPin((p) => {
          const next = normalizeStaffPin(p + e.key).slice(0, 8);
          setError("");
          if (next.length === 4) submit(next);
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [mode, busy, submit]);

  if (!mode) return null;

  const who = staffIdleLockDisplayName(user);
  const press = (key) => {
    if (busy) return;
    if (key === "←") {
      setPin((p) => p.slice(0, -1));
      setError("");
      return;
    }
    if (key === "OK") {
      submit(pin);
      return;
    }
    const next = normalizeStaffPin(pin + key).slice(0, 8);
    setPin(next);
    setError("");
    if (next.length === 4) submit(next);
  };

  return (
    <div className="hh-pin" role="dialog" aria-modal="true" aria-label="Team PIN">
      <h1 className="hh-pin__title">
        {mode === "switch"
          ? "Switch user"
          : mode === "login"
            ? "Welcome"
            : who && who !== "Team member"
              ? who
              : "Welcome"}
      </h1>
      <p className="hh-pin__hint">
        {mode === "switch" ? "Enter a team PIN" : "Enter your PIN"}
      </p>
      <div className="hh-pin__dots" aria-hidden>
        {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
          <span key={i} className={i < pin.length ? "is-on" : ""} />
        ))}
      </div>
      {error ? <p className="hh-pin__error">{error}</p> : null}
      <div className="hh-pin__pad">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={key === "OK" ? "hh-pin__key hh-pin__key--ok" : "hh-pin__key"}
            disabled={busy}
            onClick={() => press(key)}
          >
            {key === "OK" && busy ? "…" : key}
          </button>
        ))}
      </div>
      {mode === "switch" ? (
        <button type="button" className="hh-pin__cancel" onClick={unlock} disabled={busy}>
          Cancel
        </button>
      ) : null}
    </div>
  );
};

export default HandheldPinGate;
